// Solomon Portal v1 — best-effort audit event writer (frontend).
//
// Frontend audit events go through a server-side writer because portal_audit
// is service-role-only by RLS (§3.4.2). For v1 we don't have a generic audit
// Edge Function yet; the only frontend-emitted events ('user.signed_in',
// 'user.signed_out', and the `auth.error.*` family from ADR-018) are
// nice-to-have and will be backfilled in a v2 audit channel. This module is
// a stub that calls the eventual endpoint and never throws.
//
// `portal_audit.events.event_type` is `text not null` with no check
// constraint (migration 0002), so the schema accepts any event_type the
// service-role writer chooses. We codify the canonical set here so the
// callsites stay disciplined.

import { logger } from './logger';

/**
 * Canonical frontend-originated event types. Add new ones at the bottom and
 * cross-reference SPEC §3.3.2 (event taxonomy). Keep this array in lock-step
 * with the auth-error reasons in `lib/auth.tsx` (`AuthErrorReason`).
 */
export const KNOWN_AUDIT_EVENT_TYPES = [
  'user.signed_in',
  'user.signed_out',
  // Auth error taxonomy (ADR-018).
  'auth.error.link_expired',
  'auth.error.link_invalid',
  'auth.error.link_consumed',
  'auth.error.rls_failure',
  'auth.error.network_timeout',
  'auth.error.pkce_exchange_failed',
  'auth.error.unknown',
  // Operational drift / misconfiguration (ADR-015 fallback path).
  'auth.warning.legacy_hash_fragment',
  // portal-hidden tag enforcement (issue #11 / ADR-024).
  'auth.access.tag_blocked',
  'auth.access.not_authorized',
  'auth.access.not_found',
  'auth.access.granted',
  'auth.access.unauthenticated',
  'auth.access.deactivated',
  // Future list-view filter event (issue #9 will emit; catalog now per #11).
  'support.tag_filter.applied',
  // Edge Function / upstream HS errors emitted by `get-conversation`.
  'support.upstream_error',
  'support.config_error',
  // Issue #9 — server-side conversation list + thread renderer.
  'support.conversation_list.viewed',
  'support.conversation_list.filter_applied',
  'support.conversation_detail.viewed',
] as const;

export type AuditEventType = (typeof KNOWN_AUDIT_EVENT_TYPES)[number];

export interface AuditEvent {
  // Allow the canonical set + arbitrary `auth.error.*` / `auth.warning.*`
  // strings so a downstream caller can pass an event_type derived from a
  // typed reason without TypeScript collapsing the union to `string`.
  event_type: AuditEventType | (string & {});
  payload?: Record<string, unknown>;
}

export function writeAuditEvent(event: AuditEvent): Promise<void> {
  // v1: no audit endpoint exists yet; log to console only.
  // v2 will add a Supabase Edge Function 'audit-event' that validates the
  // bearer JWT and inserts into portal_audit.events as service_role.
  logger.info({
    source: 'audit',
    message: 'audit event (frontend, not yet persisted in v1)',
    payload: { event_type: event.event_type, ...(event.payload ?? {}) },
  });
  return Promise.resolve();
}
